import 'dayjs/locale/en';
import 'dayjs/locale/vi';

import { toast } from '@ahm/react-ui';
import { Button, Group, rem, Stack, Text, Tooltip } from '@mantine/core';
import { openContextModal } from '@mantine/modals';
import dayjs, { locale as setDayjsLocale, unix } from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { useBestHoursSuggestMutation } from '../../api/service/use-best-hour-suggest';
import DateTimePickerByService from './date-time-picker-by-service';
import CalendarNote from '@/assets/icons/calendar-blank-orange-icon.svg';
import { ModalName } from '@/components/modals';
import CustomComponent from '@/components/modals/modal-custom-drawer';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

function PickupTimeSelection() {
  const isMobile = useIsMobile();
  const tPickupTime = useTranslations('booking.pickup_time');
  const curLocale = useLocale();
  setDayjsLocale(curLocale);
  const {
    isLoadingStore,
    orderTime,
    setOrderTime,
    isOpenPickupTime,
    setIsOpenPickupTime,
    selectedService,
    samedaySelectedTime,
    // bestHourSuggest,
    setBestHourSuggest,
    setSamedaySelectedTime,
    estimatedRequestData,
  } = useBookingStore((state) => state);

  const { mutate: fetchBestHourSuggest } = useBestHoursSuggestMutation({
    onSuccess: (data) => {
      setBestHourSuggest(data);
      if (data?.length) {
        setSamedaySelectedTime(data[0]);
        setOrderTime(data[0].est_time);
      }
    },
    onError: (error) => {
      console.error('Error fetching best hours:', error);
      toast.error({
        title: error.message || 'Error',
        description: 'Failed to fetch best hours. Please try again.',
      });
    },
  });

  const closePickupDialog = () => {
    // const shouldAutoSetBestHour =
    //   selectedService?.suggest_earliest_block_hour &&
    //   bestHourSuggest?.length &&
    //   !samedaySelectedTime?.est_time &&
    //   !orderTime;

    // if (shouldAutoSetBestHour) {
    //   setSamedaySelectedTime(bestHourSuggest[0]);
    //   setOrderTime(bestHourSuggest[0].est_time);
    // }

    setIsOpenPickupTime(false);
  };
  const openPickupDialog = () => {
    setIsOpenPickupTime(true);
  };

  const formatTime = (orderTime: number) => {
    const now = dayjs();
    const orderDateTime = unix(Number(orderTime));

    // Check if the date is today
    if (now.isSame(orderDateTime, 'day')) {
      const formattedToday = orderDateTime.format('HH:mm');
      return tPickupTime('pickup_today', { hh_mm: formattedToday });
    } else {
      const dayOfWeek = orderDateTime.format('dd');
      const formattedDate = orderDateTime.format('DD/MM');
      const formattedTime = orderDateTime.format('HH:mm');

      const datePart = `${dayOfWeek}, ${formattedDate},`;

      return `${datePart}\n${formattedTime}`;
    }
  };

  const formatSamedayTime = (estDateTime: string) => {
    const samedayDateTime = dayjs(estDateTime);
    const now = dayjs();

    if (samedaySelectedTime?.start_time === samedaySelectedTime?.end_time) {
      return tPickupTime('pickup_now');
    }

    if (samedaySelectedTime?.start_time && samedaySelectedTime?.end_time) {
      const startTime = dayjs()
        .startOf('day')
        .add(samedaySelectedTime.start_time, 'minute')
        .format('HH:mm');
      const endTime = dayjs()
        .startOf('day')
        .add(samedaySelectedTime.end_time, 'minute')
        .format('HH:mm');

      if (now.isSame(samedayDateTime, 'day')) {
        // For today
        return tPickupTime('pickup_today_sameday', {
          start_hour: startTime,
          end_hour: endTime,
        });
      } else {
        // For future dates - break into two lines
        const dayOfWeek = samedayDateTime.format('dd');
        const formattedDate = samedayDateTime.format('DD/MM');
        const timeRange = `${startTime} - ${endTime}`;

        const datePart = `${dayOfWeek}, ${formattedDate},`;

        return `${datePart}\n${timeRange}`;
      }
    }
  };

  const formattedTime = useMemo(() => {
    if (selectedService?.service_hours && samedaySelectedTime?.est_date_time) {
      return formatSamedayTime(samedaySelectedTime.est_date_time);
    } else if (orderTime) {
      return formatTime(Number(orderTime));
    } else {
      return tPickupTime('schedule');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samedaySelectedTime, orderTime, selectedService]);

  const handleOpenPickupTime = () => {
    const isOnboarding = localStorage.getItem('isOnboarding') === null;

    if (isOnboarding) {
      handleOpenOnboarding();
      localStorage.setItem('isOnboarding', 'false');
      return;
    }

    if (isOpenPickupTime) {
      closePickupDialog();
      return;
    }

    openPickupDialog();
  };

  const handleOpenOnboarding = () => {
    openContextModal({
      modal: ModalName.OnboardingPickupTime,
      modalId: ModalName.OnboardingPickupTime,
      innerProps: {
        onConfirm: handleOpenPickupTime,
      },
      h: 'fit-content',
      withCloseButton: false,
      centered: true,
      maw: rem(440),
      w: '100%',
    });
  };

  const handleSchedule = (selectedTime: number) => {
    setOrderTime(selectedTime);
    closePickupDialog();
  };

  useEffect(() => {
    if (!estimatedRequestData?.path || estimatedRequestData.path.length < 2)
      return;

    if (selectedService?.suggest_earliest_block_hour) {
      const body = {
        path: estimatedRequestData.path,
        service_id: selectedService._id,
      };

      fetchBestHourSuggest(body);
    } else {
      setBestHourSuggest(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedService?._id,
    selectedService?.suggest_earliest_block_hour,
    estimatedRequestData?.path,
  ]);

  return (
    <>
      <Tooltip label={formattedTime} position="top" withArrow>
        <Button
          disabled={isLoadingStore}
          onClick={handleOpenPickupTime}
          justify={isMobile ? 'center' : 'left'}
          variant={isMobile ? 'white' : 'default'}
          px={isMobile ? rem(4) : rem(12)}
          py={rem(8)}
          h={isMobile ? 'auto' : rem(40)}
          fullWidth
        >
          <Stack
            gap={rem(2)}
            ta="left"
            justify="left"
            flex={1}
            style={{
              whiteSpace: 'pre-line',
              overflow: 'hidden',
            }}
          >
            <Group
              gap={rem(8)}
              align="center"
              wrap="nowrap"
              style={{
                justifyContent: isMobile ? 'center' : 'unset',
                display: isMobile ? 'flex' : 'inline-flex',
              }}
            >
              <CalendarNote />
              <Text
                c="neutral.7"
                fw={isMobile ? 700 : 500}
                size="sm"
                flex={1}
                // truncate
                lineClamp={isMobile ? 2 : 1}
              >
                {formattedTime}
              </Text>
            </Group>
          </Stack>
        </Button>
      </Tooltip>

      <CustomComponent
        title={
          selectedService?.service_hours
            ? tPickupTime('title_range')
            : tPickupTime('title')
        }
        isOpen={isOpenPickupTime}
        onClose={closePickupDialog}
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-0)`,
            paddingLeft: rem(16),
          },
          bodyStyle: {
            padding: 0,
          },
        }}
      >
        <DateTimePickerByService onSchedule={handleSchedule} />
      </CustomComponent>
    </>
  );
}

export default PickupTimeSelection;
